/**
 * Store View Utilities
 *
 * Utilities for matching region codes to store views (Miền Bắc / Miền Nam).
 * Used by checkout to validate that the shipping address belongs to the
 * currently selected store region.
 *
 * @module @kidslabs/module-core/static/store-view-utils
 */

import { RegionsStores, type RegionStoreMapping } from './regions-stores';

/**
 * Find the store mapping entry for a given region code.
 *
 * @param regionCode - Vietnamese region code (e.g., 'VN-63' for Ha Noi)
 * @returns The matching RegionStoreMapping or undefined
 */
export const findStoreViewByRegionCode = (regionCode: string): RegionStoreMapping | undefined => {
    return RegionsStores.find(store => store.region_code === regionCode);
};

/**
 * Check whether a region code belongs to the given store code.
 *
 * Example:
 * - regionCode 'VN-63' (Ha Noi) → store_code 'hn_gen_two'
 * - If currentStoreCode is 'hcm_gen_two' → returns false (mismatch)
 *
 * @param regionCode - Vietnamese region code from the shipping address
 * @param currentStoreCode - The store code currently selected by the user (e.g., 'hn_gen_two' or 'hcm_gen_two')
 * @returns true if the region belongs to the current store, false otherwise
 */
export const isRegionCodeMatchStoreCode = (regionCode: string, currentStoreCode: string): boolean => {
    const storeMapping = findStoreViewByRegionCode(regionCode);
    if (!storeMapping) {
        return false;
    }
    return storeMapping.store_code === currentStoreCode;
};
